// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * Annual Comparison Calculator
 *
 * @description Compares all tariffs and finds the most cost-effective option
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import type {
  TauronPricing,
  ConsumptionPattern,
  AnnualSummary,
  AnnualTariffResult,
  MonthlyResult,
  TariffType
} from './types';
import { DAYS_IN_MONTH, DAYS_IN_MONTH_LEAP } from './constants';
import { roundToTwoDecimals } from './helpers';
import { calculateG11 } from './g11';
import { calculateG12 } from './g12';
import { calculateG12w } from './g12w';
import { calculateG12as } from './g12as';
import { calculateG12n } from './g12n';
import { calculateG12r } from './g12r';
import { calculateG13 } from './g13';
import { calculateG13s } from './g13s';

/**
 * Calculate annual summary comparing all tariffs
 *
 * Compares all available tariffs (G11, G12, G12w, G12as, G12n, G12r, G13, G13s) across all 12 months
 * and determines the most cost-effective option with potential savings.
 *
 * @param monthlyConsumption - Array of 12 monthly values (Jan-Dec)
 * @param pricing - Tauron pricing data
 * @param pattern - Consumption pattern
 * @param year - Year for calculations
 * @returns Annual comparison with recommended tariff
 *
 * @example
 * const annual = calculateAnnualComparison(
 *   [300, 280, 260, 240, 220, 200, 220, 240, 260, 280, 300, 320],
 *   TAURON_2025_PRICING,
 *   UNIFORM_PATTERN,
 *   2025
 * );
 * // Returns: { g11: {...}, g12: {...}, g12w: {...}, ..., recommended: 'G13s', potentialSavings: 345.67, savingsPercent: 10.5 }
 */
export function calculateAnnualComparison(
  monthlyConsumption: number[],
  pricing: TauronPricing,
  pattern: ConsumptionPattern,
  year: number
): AnnualSummary {
  // Validate input
  if (monthlyConsumption.length !== 12) {
    throw new Error('Monthly consumption must have exactly 12 values (Jan-Dec)');
  }

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysPerMonth = isLeapYear ? DAYS_IN_MONTH_LEAP : DAYS_IN_MONTH;

  // Helper function to calculate annual totals for a tariff
  const calculateAnnualTotals = (results: MonthlyResult[]): AnnualTariffResult => {
    const totalKwh = results.reduce((sum, r) => sum + r.totalKwh, 0);
    const totalProduction = results.reduce((sum, r) => sum + r.productionCost, 0);
    const totalDistribution = results.reduce((sum, r) => sum + r.distributionCost, 0);
    const totalCost = results.reduce((sum, r) => sum + r.totalCost, 0);

    return {
      totalKwh: roundToTwoDecimals(totalKwh),
      totalProductionCost: roundToTwoDecimals(totalProduction),
      totalDistributionCost: roundToTwoDecimals(totalDistribution),
      totalCost: roundToTwoDecimals(totalCost),
      averageCostPerKwh: roundToTwoDecimals(totalCost / totalKwh),
      monthlyResults: results
    };
  };

  // Calculate G11 for all months
  const g11Results: MonthlyResult[] = [];
  for (let month = 0; month < 12; month++) {
    g11Results.push(calculateG11(monthlyConsumption[month], pricing.g11, month, year));
  }
  const g11Annual = calculateAnnualTotals(g11Results);

  // Calculate G12 for all months
  const g12Results: MonthlyResult[] = [];
  for (let month = 0; month < 12; month++) {
    g12Results.push(calculateG12(
      monthlyConsumption[month],
      pricing.g12,
      pattern,
      daysPerMonth[month],
      year,
      month
    ));
  }
  const g12Annual = calculateAnnualTotals(g12Results);

  // Calculate G12w if pricing available
  let g12wAnnual: AnnualTariffResult | undefined;
  if (pricing.g12w) {
    const g12wResults: MonthlyResult[] = [];
    for (let month = 0; month < 12; month++) {
      g12wResults.push(calculateG12w(
        monthlyConsumption[month],
        pricing.g12w,
        pattern,
        daysPerMonth[month],
        year,
        month
      ));
    }
    g12wAnnual = calculateAnnualTotals(g12wResults);
  }

  // Calculate G12as if pricing available
  let g12asAnnual: AnnualTariffResult | undefined;
  if (pricing.g12as) {
    const g12asResults: MonthlyResult[] = [];
    for (let month = 0; month < 12; month++) {
      g12asResults.push(calculateG12as(
        monthlyConsumption[month],
        pricing.g12as,
        pattern,
        daysPerMonth[month],
        year,
        month
      ));
    }
    g12asAnnual = calculateAnnualTotals(g12asResults);
  }

  // Calculate G12n if pricing available (PGE-specific)
  let g12nAnnual: AnnualTariffResult | undefined;
  if (pricing.g12n) {
    const g12nResults: MonthlyResult[] = [];
    for (let month = 0; month < 12; month++) {
      g12nResults.push(calculateG12n(
        monthlyConsumption[month],
        pricing.g12n,
        pattern,
        daysPerMonth[month],
        year,
        month
      ));
    }
    g12nAnnual = calculateAnnualTotals(g12nResults);
  }

  // Calculate G12r if pricing available (Energa-specific)
  let g12rAnnual: AnnualTariffResult | undefined;
  if (pricing.g12r) {
    const g12rResults: MonthlyResult[] = [];
    for (let month = 0; month < 12; month++) {
      g12rResults.push(calculateG12r(
        monthlyConsumption[month],
        pricing.g12r,
        pattern,
        daysPerMonth[month],
        year,
        month
      ));
    }
    g12rAnnual = calculateAnnualTotals(g12rResults);
  }

  // Calculate G13 if pricing available
  let g13Annual: AnnualTariffResult | undefined;
  if (pricing.g13) {
    const g13Results: MonthlyResult[] = [];
    for (let month = 0; month < 12; month++) {
      g13Results.push(calculateG13(
        monthlyConsumption[month]!,
        pricing.g13,
        pattern,
        daysPerMonth[month]!,
        year,
        month
      ));
    }
    g13Annual = calculateAnnualTotals(g13Results);
  }

  // Calculate G13s if pricing available
  let g13sAnnual: AnnualTariffResult | undefined;
  if (pricing.g13s) {
    const g13sResults: MonthlyResult[] = [];
    for (let month = 0; month < 12; month++) {
      g13sResults.push(calculateG13s(
        monthlyConsumption[month]!,
        pricing.g13s,
        pattern,
        daysPerMonth[month]!,
        year,
        month
      ));
    }
    g13sAnnual = calculateAnnualTotals(g13sResults);
  }

  // Find cheapest tariff
  const tariffCosts: Array<{ tariff: TariffType; cost: number }> = [
    { tariff: 'G11', cost: g11Annual.totalCost },
    { tariff: 'G12', cost: g12Annual.totalCost }
  ];

  if (g12wAnnual) tariffCosts.push({ tariff: 'G12w', cost: g12wAnnual.totalCost });
  if (g12asAnnual) tariffCosts.push({ tariff: 'G12as', cost: g12asAnnual.totalCost });
  if (g12nAnnual) tariffCosts.push({ tariff: 'G12n', cost: g12nAnnual.totalCost });
  if (g12rAnnual) tariffCosts.push({ tariff: 'G12r', cost: g12rAnnual.totalCost });
  if (g13Annual) tariffCosts.push({ tariff: 'G13', cost: g13Annual.totalCost });
  if (g13sAnnual) tariffCosts.push({ tariff: 'G13s', cost: g13sAnnual.totalCost });

  // Sort by cost (ascending)
  tariffCosts.sort((a, b) => a.cost - b.cost);

  const cheapest = tariffCosts[0];
  const mostExpensive = tariffCosts[tariffCosts.length - 1];
  const potentialSavings = mostExpensive.cost - cheapest.cost;
  const savingsPercent = (potentialSavings / mostExpensive.cost) * 100;
  const savingsVsG11 = g11Annual.totalCost - cheapest.cost;

  return {
    g11: g11Annual,
    g12: g12Annual,
    g12w: g12wAnnual,
    g12as: g12asAnnual,
    g12n: g12nAnnual,
    g12r: g12rAnnual,
    g13: g13Annual,
    g13s: g13sAnnual,
    recommended: cheapest.tariff,
    potentialSavings: roundToTwoDecimals(potentialSavings),
    savingsPercent: roundToTwoDecimals(savingsPercent),
    savingsVsG11: roundToTwoDecimals(savingsVsG11)
  };
}
